import type {
  CampaignCreationTask,
  SmartCreationBrowserDraft,
  SmartCreationDraft,
  SmartCreationStrategyOperation,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TimeoutError } from "ky";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCampaignCreationTaskScope } from "@/hooks/use-campaign-creation-tasks";
import { fetchAmazonConnectUrl } from "@/lib/amazon-api";
import { getApiErrorCode, getApiErrorDetails, getApiErrorMessage } from "@/lib/api-error";
import { parseCampaignCreationInvalidProducts } from "@/lib/campaign-creation-session";
import {
  CAMPAIGN_CREATION_TASK_QUERY_KEY,
  campaignCreationTaskKey,
} from "@/lib/campaign-creation-tasks";
import {
  acceptSmartCreationStrategyRecommendation,
  cancelSmartCreationPlan,
  discardSmartCreationPendingSubmission,
  generateSmartCreationPreviewPlan,
  generateSmartCreationStrategyRecommendation,
  previewSmartCreationObjectiveRecommendation,
  refreshPreviewSmartCreationReviewWorkspace,
  submitSmartCreationDraft,
  updateSmartCreationPlan,
  updateSmartCreationStrategy,
} from "@/lib/smart-creation-api";
import { readLocalSmartCreationPlan } from "@/lib/smart-creation-draft-store";
import { claimSmartCreationActionLock, smartCreationPlanQueryKey } from "./plan-queries";
import type { ExitGuard } from "./use-exit-guard";
import type { PlanSession } from "./use-plan-session";
import type { StepInputs } from "./use-step-inputs";

interface PlanCommand {
  identity: string;
  currentPlan: SmartCreationBrowserDraft;
}

interface WorkspaceMutationsOptions {
  session: PlanSession;
  stepInputs: StepInputs;
  exitGuard: ExitGuard;
  planId: string;
  shopId: string;
  closeCancelDialog: () => void;
  onClose: () => void | Promise<void>;
  onTaskAccepted?: (task: CampaignCreationTask) => void | Promise<void>;
}

/**
 * Every durable Plan command. Each runs under the session action lock claimed by
 * `beginAction`, ignores settlements for a Plan the workspace has since left, and releases
 * the lock when it settles.
 */
export function useWorkspaceMutations({
  session,
  stepInputs,
  exitGuard,
  planId,
  shopId,
  closeCancelDialog,
  onClose,
  onTaskAccepted,
}: WorkspaceMutationsOptions) {
  const { t } = useTranslation("smartCreation");
  const queryClient = useQueryClient();
  const taskScope = useCampaignCreationTaskScope();
  const {
    currentIdentityRef,
    editVersionRef,
    handleDurableActionError,
    handleRevisionConflict,
    releaseActionLock,
    setPlan,
  } = session;
  const reportDurableError = (error: unknown, variables: PlanCommand) =>
    handleDurableActionError(error, {
      identity: variables.identity,
      planId: variables.currentPlan.id,
      shopId: variables.currentPlan.shopId,
    });
  const releaseLock = (_data: unknown, _error: unknown, variables: PlanCommand) =>
    releaseActionLock(variables.identity);

  const finishAcceptedTask = async (task: CampaignCreationTask, identity: string) => {
    queryClient.setQueryData(campaignCreationTaskKey(taskScope, task.shopId, task.id), task);
    void queryClient.invalidateQueries({ queryKey: CAMPAIGN_CREATION_TASK_QUERY_KEY });
    exitGuard.confirmedCloseRef.current = true;
    releaseActionLock(identity);
    if (onTaskAccepted) await onTaskAccepted(task);
    else await onClose();
  };

  // A strategy change made from the preview must regenerate it; the saved strategy is
  // committed first so a failed regeneration still reports against the new revision.
  const regeneratePreviewAfterStrategySave = async (
    identity: string,
    currentPlan: SmartCreationBrowserDraft,
    updated: SmartCreationBrowserDraft,
  ) => {
    queryClient.setQueryData(smartCreationPlanQueryKey(updated.shopId, updated.id), updated);
    if (currentPlan.draft.currentStep !== "preview") return updated;
    if (identity === currentIdentityRef.current) setPlan(updated);
    try {
      return await generateSmartCreationPreviewPlan(updated.id, updated.shopId, updated.revision);
    } catch (error) {
      await handleDurableActionError(error, {
        identity,
        planId: updated.id,
        shopId: updated.shopId,
      });
      // The saved strategy was already committed above; keep the reported error visible.
      return null;
    }
  };
  const commitStrategyResult = (
    nextPlan: SmartCreationBrowserDraft | null,
    variables: PlanCommand,
  ) => {
    if (nextPlan && variables.identity === currentIdentityRef.current) setPlan(nextPlan);
  };

  const stepMutation = useMutation({
    mutationFn: ({ currentPlan, nextDraft }: PlanCommand & { nextDraft: SmartCreationDraft }) =>
      updateSmartCreationPlan(currentPlan.id, currentPlan.shopId, currentPlan.revision, nextDraft),
    onSuccess: (nextPlan, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      setPlan(nextPlan);
    },
    onError: async (error, variables) => {
      const code = await getApiErrorCode(error);
      if (
        code === "campaign_creation.product_selection_invalid" &&
        variables.currentPlan.draft.currentStep === "products" &&
        variables.nextDraft.currentStep === "objectives"
      ) {
        const products = parseCampaignCreationInvalidProducts(await getApiErrorDetails(error));
        if (products.length > 0) {
          stepInputs.setInvalidProducts(products);
        } else {
          toast.error(t("errors.productInvalid"));
        }
        return;
      }
      if (
        variables.currentPlan.draft.currentStep === "products" &&
        variables.nextDraft.currentStep === "objectives"
      ) {
        const conflictHandled = await handleRevisionConflict(error, {
          identity: variables.identity,
          planId: variables.currentPlan.id,
          shopId: variables.currentPlan.shopId,
        });
        if (!conflictHandled) {
          const message =
            error instanceof TypeError || error instanceof TimeoutError
              ? t("errors.action")
              : await getApiErrorMessage(error);
          toast.error(message);
        }
        return;
      }
      await reportDurableError(error, variables);
    },
    onSettled: releaseLock,
  });
  const generatePreviewMutation = useMutation({
    mutationFn: async ({
      identity,
      currentPlan,
      nextDraft,
      stepAlreadySaved,
    }: PlanCommand & { nextDraft: SmartCreationDraft; stepAlreadySaved?: boolean }) => {
      const updated = stepAlreadySaved
        ? currentPlan
        : await updateSmartCreationPlan(
            currentPlan.id,
            currentPlan.shopId,
            currentPlan.revision,
            nextDraft,
          );
      if (!stepAlreadySaved) {
        // Cache the successful save immediately so a generatePreview failure cannot
        // leave this client retrying with a stale revision.
        queryClient.setQueryData(smartCreationPlanQueryKey(updated.shopId, updated.id), updated);
        if (identity === currentIdentityRef.current) {
          setPlan(updated);
        }
      }
      return generateSmartCreationPreviewPlan(updated.id, updated.shopId, updated.revision);
    },
    onSuccess: (nextPlan, variables) => {
      if (variables.identity === currentIdentityRef.current) setPlan(nextPlan);
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const refreshPreviewReviewMutation = useMutation({
    mutationFn: ({ currentPlan }: PlanCommand) => {
      const workspace = currentPlan.reviewWorkspace;
      if (!workspace) throw new Error(t("errors.action"));
      return refreshPreviewSmartCreationReviewWorkspace(currentPlan.id, currentPlan.shopId, {
        expectedPlanRevision: currentPlan.revision,
      });
    },
    onSuccess: (nextPlan, variables) => {
      if (variables.identity === currentIdentityRef.current) setPlan(nextPlan);
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const confirmMutation = useMutation({
    mutationFn: ({ currentPlan }: PlanCommand) => {
      const workspace = currentPlan.reviewWorkspace;
      if (!workspace) throw new Error(t("errors.action"));
      return submitSmartCreationDraft(currentPlan.id, currentPlan.shopId, {
        expectedPlanRevision: currentPlan.revision,
        expectedWorkspaceRevision: workspace.workspaceRevision,
        contentHash: workspace.contentHash,
      });
    },
    onSuccess: async (task, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      await finishAcceptedTask(task, variables.identity);
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const discardSubmissionMutation = useMutation({
    mutationFn: ({ currentPlan }: PlanCommand) =>
      discardSmartCreationPendingSubmission(currentPlan.id, currentPlan.shopId),
    onSuccess: async (task, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      if (task) await finishAcceptedTask(task, variables.identity);
      else toast.success(t("task.submissionDiscarded"));
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const reconnectMutation = useMutation({
    mutationFn: (_variables: PlanCommand) => fetchAmazonConnectUrl(),
    onSuccess: ({ redirectUrl }, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      window.location.href = redirectUrl;
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const recommendationMutation = useMutation({
    mutationFn: ({
      initialDraft,
    }: PlanCommand & { editVersion: number; initialDraft: SmartCreationDraft }) =>
      previewSmartCreationObjectiveRecommendation(planId, shopId, initialDraft),
    onSuccess: (recommendation, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      if (variables.editVersion === editVersionRef.current)
        stepInputs.setObjectiveRecommendation(recommendation);
    },
    onError: async (error, variables) => {
      const request = {
        identity: variables.identity,
        planId: variables.currentPlan.id,
        shopId: variables.currentPlan.shopId,
      };
      const conflictHandled = await handleRevisionConflict(error, request);
      if (!conflictHandled && request.identity === currentIdentityRef.current) {
        toast.error(await getApiErrorMessage(error));
      }
    },
  });
  const strategyMutation = useMutation({
    mutationFn: async ({
      identity,
      currentPlan,
      expectedRevision,
      operations,
    }: PlanCommand & {
      expectedRevision: number;
      operations: SmartCreationStrategyOperation[];
    }) => {
      const updated = await updateSmartCreationStrategy(currentPlan.id, currentPlan.shopId, {
        expectedRevision,
        operations,
      });
      return regeneratePreviewAfterStrategySave(identity, currentPlan, updated);
    },
    onSuccess: commitStrategyResult,
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const strategyRecommendationMutation = useMutation({
    mutationFn: async ({ identity, currentPlan }: PlanCommand) => {
      const updated = await generateSmartCreationStrategyRecommendation(
        currentPlan.id,
        currentPlan.shopId,
        currentPlan.revision,
      );
      return regeneratePreviewAfterStrategySave(identity, currentPlan, updated);
    },
    onSuccess: commitStrategyResult,
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const acceptStrategyRecommendationMutation = useMutation({
    mutationFn: async ({
      identity,
      currentPlan,
      expectedRevision,
      strategyKind,
    }: PlanCommand & {
      expectedRevision: number;
      strategyKind: SmartCreationTargetingStrategyKind;
    }) => {
      const recommendation = currentPlan.strategyRecommendation;
      if (!recommendation) throw new Error(t("errors.recommendationUnavailable"));
      const updated = await acceptSmartCreationStrategyRecommendation(
        currentPlan.id,
        currentPlan.shopId,
        {
          expectedRevision,
          recommendationId: recommendation.id,
          strategies: [strategyKind],
        },
      );
      return regeneratePreviewAfterStrategySave(identity, currentPlan, updated);
    },
    onSuccess: commitStrategyResult,
    onError: reportDurableError,
    onSettled: releaseLock,
  });
  const cancelMutation = useMutation({
    mutationFn: ({ currentPlan }: PlanCommand) =>
      cancelSmartCreationPlan(currentPlan.id, currentPlan.shopId, currentPlan.revision),
    onSuccess: (_data, variables) => {
      if (variables.identity !== currentIdentityRef.current) return;
      session.strategySubviewDirtyRef.current = false;
      session.setStrategySubviewDirty(false);
      session.handleReviewDirtyChange(false);
      closeCancelDialog();
      exitGuard.completeClose();
    },
    onError: reportDurableError,
    onSettled: releaseLock,
  });

  const planRevision = session.plan?.revision;
  const planUpdatedAt = session.plan?.updatedAt;
  // Submission state changes only through these mutations or a plan reload; avoid a storage read per render.
  const publicationPending = useMemo(() => {
    void [planRevision, planUpdatedAt, confirmMutation.status, discardSubmissionMutation.status];
    return Boolean(readLocalSmartCreationPlan(planId, shopId)?.submission);
  }, [
    planId,
    shopId,
    planRevision,
    planUpdatedAt,
    confirmMutation.status,
    discardSubmissionMutation.status,
  ]);

  const beginAction = (options: { blockContent?: boolean; flushStructure?: boolean } = {}) => {
    if (
      session.reviewQueueRef.current?.isPending() ||
      session.revisionConflictRef.current ||
      (!options.flushStructure && session.structureQueueRef.current?.isPending())
    )
      return null;
    const identity = claimSmartCreationActionLock(
      session.actionLock,
      currentIdentityRef.current,
      session.mountedRef.current,
    );
    if (!identity) return null;
    session.setActionSerializing(true);
    // Serialization protects the Plan revision. Whole-step blocking is a
    // separate presentation choice: field-level mutations keep the surface
    // stable and expose their progress only at the initiating control/footer.
    session.setContentBlocking(options.blockContent ?? true);
    stepInputs.setValidationError(null);
    session.setDurableActionError(null);
    stepMutation.reset();
    generatePreviewMutation.reset();
    refreshPreviewReviewMutation.reset();
    confirmMutation.reset();
    discardSubmissionMutation.reset();
    reconnectMutation.reset();
    recommendationMutation.reset();
    strategyMutation.reset();
    strategyRecommendationMutation.reset();
    acceptStrategyRecommendationMutation.reset();
    return identity;
  };

  return {
    stepMutation,
    generatePreviewMutation,
    refreshPreviewReviewMutation,
    confirmMutation,
    discardSubmissionMutation,
    reconnectMutation,
    recommendationMutation,
    strategyMutation,
    strategyRecommendationMutation,
    acceptStrategyRecommendationMutation,
    cancelMutation,
    publicationPending,
    beginAction,
  };
}

export type WorkspaceMutations = ReturnType<typeof useWorkspaceMutations>;
